import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { diagnosticoAPI, alertsAPI } from '../services/api';
import { api } from '../api/client.js';
import { FEELINGS } from '../utils/constants';
import { normalizeQuestionsBank, STATIC_QUESTIONS_BANK } from '../utils/coachQuestionsBank.js';
import { summarizeSupervisorAlerts } from '../utils/supervisorAlerts.js';
import SupervisorProfileCard from '../components/SupervisorProfileCard.jsx';
import SupervisorWorkerPanel from '../components/SupervisorWorkerPanel.jsx';
import SupervisorManadaPanel from '../components/SupervisorManadaPanel.jsx';
import SupervisorAlertsPanel from '../components/SupervisorAlertsPanel.jsx';
import SupervisorObservadorPanel from '../components/SupervisorObservadorPanel.jsx';
import './SupervisorDashboard.css';

export default function SupervisorDashboard() {
    const { user } = useAuth();
    const [teamData, setTeamData] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [questionsBank, setQuestionsBank] = useState(null);
    const [leaderProfile, setLeaderProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [centerTab, setCenterTab] = useState('manada');

    useEffect(() => {
        setCenterTab('manada');
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [teamRes, alertsRes, questionsRes, profileRes] = await Promise.all([
                diagnosticoAPI.getEquipo(user.id),
                alertsAPI.getMyAlerts(),
                api.getPowerfulQuestions().catch((err) => {
                    console.error('Banco de preguntas (precarga):', err);
                    return null;
                }),
                api.getLeaderProfile().catch(() => null),
            ]);
            setTeamData(teamRes.data.data || []);
            setAlerts(alertsRes.data.data || []);
            setQuestionsBank(
                normalizeQuestionsBank(questionsRes) ?? STATIC_QUESTIONS_BANK
            );
            const prof = profileRes?.data || profileRes;
            setLeaderProfile(prof?.leadership_style || prof?.style ? prof : null);
        } catch (err) {
            console.error('Error loading supervisor data:', err);
        } finally {
            setLoading(false);
        }
    };

    const n = teamData.length || 0;
    const bien = teamData.filter((t) => t.estado_hoy === 'bien').length;
    const regular = teamData.filter((t) => t.estado_hoy === 'regular').length;
    const mal = teamData.filter((t) => t.estado_hoy === 'mal').length;
    const tensionPct =
        n > 0 ? Math.min(100, Math.round((teamData.filter((t) => t.estado_hoy !== 'bien').length / n) * 100)) : 0;

    const selectedWorker = teamData.find((w) => String(w.id) === String(selectedId)) || null;

    const alertStats = useMemo(() => summarizeSupervisorAlerts(alerts), [alerts]);

    function selectWorker(id) {
        const sid = String(id);
        setSelectedId((prev) => (String(prev) === sid ? null : id));
    }

    function setCenterTabSafe(tab) {
        setCenterTab(tab);
    }

    if (loading) return <div className="sup-loading">Cargando equipo…</div>;

    const teamSummary = { bien, regular, mal, tensionPct, n };

    return (
        <div className="sup-shell sup-shell-3col">
            {/* Columna 1 — Perfil, liderazgo, preguntas y semáforo */}
            <div className="sup-col sup-col-resumen">
                <div className="sup-col-inner">
                    <SupervisorProfileCard
                        teamSummary={teamSummary}
                        alerts={alerts}
                        leaderProfile={leaderProfile}
                        questionsBank={questionsBank}
                    />
                </div>
            </div>

            {/* Columna 2 — Manada, alertas o seguimiento */}
            <div className="sup-col sup-col-centro">
                <div className="sup-col-inner">
                    <div className="sup-center-tabs" role="tablist" aria-label="Vista central">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={centerTab === 'manada'}
                            className={`sup-center-tab${centerTab === 'manada' ? ' on' : ''}`}
                            onClick={() => setCenterTabSafe('manada')}
                        >
                            🐺 Manada
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={centerTab === 'alertas'}
                            className={`sup-center-tab${centerTab === 'alertas' ? ' on' : ''}`}
                            onClick={() => setCenterTabSafe('alertas')}
                        >
                            🚨 Alertas
                            {alertStats.total > 0 && (
                                <span
                                    className="sup-center-tab-badge"
                                    title={`${alertStats.total} alertas · ${alertStats.people} personas`}
                                >
                                    {alertStats.total}
                                </span>
                            )}
                        </button>
                    </div>

                    {centerTab === 'manada' && (
                        <section aria-label="Comprensión de manada">
                            <p className="sup-section-label">Comprensión de manada</p>
                            <SupervisorManadaPanel teamData={teamData} onSelectWorker={selectWorker} />
                        </section>
                    )}

                    {centerTab === 'alertas' && (
                        <section className="sup-alerts" aria-label="Gestión de alertas">
                            <p className="sup-section-label">Gestión de alertas</p>
                            {alertStats.total > 0 && (
                                <p className="sup-team-hint">
                                    {alertStats.people} persona{alertStats.people !== 1 ? 's' : ''} con{' '}
                                    {alertStats.total} señal{alertStats.total !== 1 ? 'es' : ''} activa
                                    {alertStats.total !== 1 ? 's' : ''} (de {n} en tu equipo).
                                </p>
                            )}
                            <SupervisorAlertsPanel
                                alerts={alerts}
                                teamData={teamData}
                                onSelectPerson={setSelectedId}
                                onAlertHandled={fetchData}
                            />
                        </section>
                    )}
                </div>
            </div>

            {/* Columna 3 — Personas y seguimiento EPA */}
            <div className="sup-col sup-col-personas">
                <div className="sup-col-inner sup-col-personas-inner">
                    <section className="sup-team" aria-label="Personas">
                        <p className="sup-section-label">Personas</p>
                        <p className="sup-team-hint">Toca un nombre para ver estado y EPA de seguimiento.</p>
                        <ul className="sup-team-list">
                            {teamData.map((worker) => {
                                const hasAlert = alerts.some(
                                    (a) => String(a.employee_id ?? a.trabajador_id) === String(worker.id)
                                );
                                const isOn = String(selectedId) === String(worker.id);
                                const quitRisk = worker.thought_of_quitting === 'si_mucho';
                                return (
                                    <li key={worker.id}>
                                        <button
                                            type="button"
                                            className={`sup-team-row${hasAlert ? ' sup-team-row-alert' : ''}${isOn ? ' sup-team-row-on' : ''}${quitRisk ? ' sup-team-row-quit' : ''}`}
                                            onClick={() => selectWorker(worker.id)}
                                        >
                                            <span className="sup-team-mood" aria-hidden>
                                                {FEELINGS[worker.estado_hoy]?.emoji || '·'}
                                            </span>
                                            <span className="sup-team-name">{worker.nombre.split(' ')[0]}</span>
                                            {quitRisk && <span className="sup-team-flag quit">Huida</span>}
                                            {hasAlert && !quitRisk && (
                                                <span className="sup-team-flag">Atención</span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        {n === 0 && (
                            <p className="sup-team-hint">Cuando tengas colaboradores asignados, aparecerán aquí.</p>
                        )}
                    </section>

                    {selectedId ? (
                        <section className="sup-person-detail" aria-label="Seguimiento de persona">
                            <SupervisorWorkerPanel
                                workerId={selectedId}
                                workerRow={selectedWorker}
                                onClose={() => setSelectedId(null)}
                            />
                        </section>
                    ) : (
                        <>
                            <SupervisorObservadorPanel onCompleted={fetchData} />
                            <p className="sup-team-hint sup-person-placeholder">
                                Elige una persona de la lista para ver su seguimiento EPA.
                            </p>
                        </>
                    )}

                    {selectedId ? (
                        <SupervisorObservadorPanel
                            compact
                            viewingWorkerName={selectedWorker?.nombre?.split(' ')[0]}
                            onCompleted={fetchData}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}
